import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, BookOpen, CheckCircle2, Copy, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { ProdHeader, StatusBadge, money, money2, fmt, useRoles, errMsg, fmtData, useFabrica } from "./shared";
import { PrecoBox, ComponentesAviso, MargemPadrao } from "./CustoPreco";

function CustoDetalhe({ f }) {
  return (
    <div className="space-y-2 md:col-span-2" data-testid={`ficha-memoria-${f.id}`}>
      <div className="overflow-x-auto"><table className="w-full text-xs">
        <thead><tr className="text-muted-foreground text-left"><th className="py-1">Ingrediente</th><th className="text-right">Qtd</th><th className="text-right">Perda</th><th className="text-right pr-3">Qtd bruta</th><th className="pl-3">Conversão</th><th>Origem do custo</th><th className="text-right">Custo/un</th><th className="text-right">Custo</th></tr></thead>
        <tbody>{f.custo.linhas.map((l) => (
          <tr key={l.item_id} className="border-t border-border/50" data-testid={`ficha-linha-${l.codigo}`}>
            <td className="py-1">{l.nome}</td><td className="text-right font-mono">{fmt(l.qtd)} {l.unidade}</td><td className="text-right">{fmt(l.perda_pct || 0, 2)}%</td>
            <td className="text-right font-mono pr-3">{fmt(l.qtd_bruta)} {l.unidade}</td>
            <td className="pl-3">{l.tipo === "INTERMEDIARIO" ? "—" : `1 ${l.unidade_compra} = ${fmt(l.fator_conversao)} ${l.unidade} (${money(l.preco_compra)}/${l.unidade_compra})`}</td>
            <td data-testid={`ficha-origem-${l.codigo}`}>{l.custo_origem_txt}</td>
            <td className="text-right font-mono">{money(l.custo_unit)}</td><td className="text-right font-mono" data-testid={`ficha-custo-linha-${l.codigo}`}>{money2(l.custo)}</td>
          </tr>))}
          <tr className="border-t border-border font-semibold"><td colSpan={7} className="py-1">Custo da receita (rende {fmt(f.rendimento_qtd)} {f.unidade})</td><td className="text-right font-mono">{money2(f.custo.total)}</td></tr>
          <tr><td colSpan={7}>Custo por {f.unidade} = {money2(f.custo.total)} ÷ {fmt(f.rendimento_qtd)}</td><td className="text-right font-mono" data-testid={`ficha-custo-un-${f.id}`}>{money(f.custo.por_unidade)}</td></tr>
        </tbody></table></div>
      <div className="text-[11px] text-muted-foreground">Critério: {f.custo.criterio}.</div>
      <ComponentesAviso texto={f.custo.componentes} testid={`ficha-componentes-${f.id}`} />
    </div>
  );
}

const EMPTY = { produto_id: "", rendimento_qtd: 100, peso_unidade: 0, modo_preparo: "", responsavel: "", observacao: "", itens: [] };

function Simulador({ ficha }) {
  const [q, setQ] = useState(ficha.rendimento_qtd * 5);
  const fator = q / (ficha.rendimento_qtd || 1);
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="simulador">
      <div className="flex items-center gap-2 text-xs font-bold uppercase text-primary"><Calculator className="w-4 h-4" /> Simular necessidade</div>
      <div className="flex items-center gap-2 text-sm"><span>Produzir</span><Input type="number" className="w-28 h-8" value={q} onChange={(e) => setQ(Number(e.target.value))} data-testid="sim-qtd" /><span>{ficha.unidade} = <b>{fmt(fator)}</b> receitas</span></div>
      <div className="grid gap-1 text-xs">{ficha.custo.linhas.map((l) => <div key={l.item_id} className="flex justify-between"><span>{l.nome}</span><span className="font-mono">{fmt(l.qtd_bruta * fator)} {l.unidade}</span></div>)}</div>
      <div className="flex justify-between text-sm border-t border-border pt-2"><span>Custo previsto</span><b className="font-mono" data-testid="sim-custo">{money2(ficha.custo.total * fator)}</b></div>
    </div>
  );
}

function FichaCard({ f, tec, aprov, onEdit, onAction }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="bg-card/70 p-4 space-y-3" data-testid={`ficha-${f.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><span className="font-semibold">{f.produto_nome}</span><span className="font-mono text-xs text-primary">v{f.versao}</span><StatusBadge s={f.status} testid={`ficha-status-${f.id}`} /></div>
          <div className="text-xs text-muted-foreground">Rende {fmt(f.rendimento_qtd)} {f.unidade} · {f.itens.length} ingredientes · Resp.: {f.responsavel || "—"} · {fmtData(f.created_at)}{f.aprovado_por ? ` · aprovada por ${f.aprovado_por}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold" data-testid={`ficha-custo-${f.id}`}>{money2(f.custo.total)}</div>
          <div className="text-xs text-muted-foreground">{money(f.custo.por_unidade)} / {f.unidade}</div>
          {f.precificacao?.preco_sugerido != null && <div className="text-xs text-primary" data-testid={`ficha-preco-${f.id}`}>preço sugerido {money(f.precificacao.preco_sugerido)} ({fmt(f.precificacao.margem_pct, 2)}% s/ venda)</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`ficha-detalhe-${f.id}`}>{open ? "Ocultar" : "Ver ingredientes"}</Button>
        {tec && f.status === "RASCUNHO" && <Button size="sm" variant="outline" onClick={() => onEdit(f)} data-testid={`ficha-edit-${f.id}`}><Pencil className="w-4 h-4" /> Editar</Button>}
        {tec && f.status === "RASCUNHO" && <Button size="sm" variant="outline" className="text-red-400" onClick={() => onAction(f, "excluir")} data-testid={`ficha-excluir-${f.id}`}><Trash2 className="w-4 h-4" /></Button>}
        {aprov && f.status === "RASCUNHO" && <Button size="sm" onClick={() => onAction(f, "aprovar")} data-testid={`ficha-aprovar-${f.id}`}><CheckCircle2 className="w-4 h-4" /> Aprovar</Button>}
        {tec && f.status !== "RASCUNHO" && <Button size="sm" variant="outline" onClick={() => onAction(f, "nova-versao")} data-testid={`ficha-versao-${f.id}`}><Copy className="w-4 h-4" /> Nova versão</Button>}
      </div>
      {open && (
        <div className="grid md:grid-cols-2 gap-3">
          <CustoDetalhe f={f} />
          <div className="space-y-1 text-sm">
            <PrecoBox p={f.precificacao} testid={`ficha-precobox-${f.id}`} />
            {f.modo_preparo && <div className="text-xs text-muted-foreground whitespace-pre-wrap pt-2"><b>Modo de preparo:</b> {f.modo_preparo}</div>}
          </div>
          <Simulador ficha={f} />
        </div>
      )}
    </Card>
  );
}

function IngredientRows({ form, setForm, itens }) {
  const upd = (i, k, v) => setForm((f) => ({ ...f, itens: f.itens.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const comps = itens.filter((i) => i.id !== form.produto_id && i.ativo !== false);
  return (
    <div className="sm:col-span-2 space-y-2">
      <div className="flex items-center justify-between"><Label>Ingredientes e embalagens (na unidade de consumo)</Label>
        <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, itens: [...f.itens, { item_id: "", qtd: 0, perda_pct: 0 }] }))} data-testid="ficha-add-ing"><Plus className="w-4 h-4" /> Ingrediente</Button></div>
      {form.itens.map((x, i) => {
        const it = itens.find((y) => y.id === x.item_id);
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ficha-ing-${i}`}>
            <div className="col-span-6"><Select value={x.item_id} onValueChange={(v) => upd(i, "item_id", v)}><SelectTrigger data-testid={`ficha-ing-item-${i}`}><SelectValue placeholder="Material" /></SelectTrigger>
              <SelectContent>{comps.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} ({c.unidade_consumo}){c.tipo === "INTERMEDIARIO" ? " · intermediário" : ""}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-3 flex items-center gap-1"><Input type="number" value={x.qtd} onChange={(e) => upd(i, "qtd", Number(e.target.value))} data-testid={`ficha-ing-qtd-${i}`} /><span className="text-xs text-muted-foreground">{it?.unidade_consumo}</span></div>
            <div className="col-span-2"><Input type="number" placeholder="% perda" value={x.perda_pct} onChange={(e) => upd(i, "perda_pct", Number(e.target.value))} data-testid={`ficha-ing-perda-${i}`} /></div>
            <Button size="icon" variant="ghost" className="col-span-1 text-red-400" onClick={() => setForm((f) => ({ ...f, itens: f.itens.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        );
      })}
    </div>
  );
}

export default function ProducaoFichas() {
  const { tec, aprov } = useRoles();
  const [fichas, setFichas] = useState(null);
  const [itens, setItens] = useState([]);
  const [form, setForm] = useState(null);
  const [verObs, setVerObs] = useState(false);
  const [saving, setSaving] = useState(false);
  const fab = useFabrica();
  const load = () => api.get("/producao/fichas", { params: { unit_id: fab.unitId || undefined } }).then((r) => setFichas(r.data));
  useEffect(() => { if (fab.unidades) load(); }, [fab.unitId, fab.unidades]); // eslint-disable-line
  useEffect(() => { api.get("/producao/itens").then((r) => setItens(r.data)); }, []);
  const produtos = useMemo(() => itens.filter((i) => ["INTERMEDIARIO", "ACABADO"].includes(i.tipo)), [itens]);
  const prodSel = itens.find((i) => i.id === form?.produto_id);

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/producao/fichas/${form.id}`, form); else await api.post("/producao/fichas", form);
      toast.success("Ficha salva como rascunho"); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const action = async (f, a) => {
    if (a === "excluir") {
      if (!window.confirm(`Excluir rascunho v${f.versao} de ${f.produto_nome}?`)) return;
      try { await api.delete(`/producao/fichas/${f.id}`); toast.success("Rascunho excluído"); load(); } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    try { await api.post(`/producao/fichas/${f.id}/${a}`); toast.success(a === "aprovar" ? "Ficha aprovada — versões anteriores ficaram obsoletas" : "Nova versão criada como rascunho"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const lista = (fichas || []).filter((f) => verObs || f.status !== "OBSOLETA");

  return (
    <div className="space-y-5" data-testid="prod-fichas-page">
      <ProdHeader title="Fichas Técnicas" icon={BookOpen} fab={fab}>
        <MargemPadrao aprov={aprov} />
        <Button variant="outline" size="sm" onClick={() => setVerObs(!verObs)} data-testid="toggle-obsoletas">{verObs ? "Ocultar obsoletas" : "Mostrar histórico de versões"}</Button>
        {tec && <Button onClick={() => setForm({ ...EMPTY })} data-testid="btn-nova-ficha"><Plus className="w-4 h-4" /> Nova ficha</Button>}
      </ProdHeader>
      {!fichas ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid gap-3">
          {lista.map((f) => <FichaCard key={f.id} f={f} tec={tec} aprov={aprov} onEdit={(x) => setForm(x)} onAction={action} />)}
          {!lista.length && <Card className="p-10 text-center text-muted-foreground">Nenhuma ficha técnica. Cadastre primeiro os itens (matérias-primas e produtos).</Card>}
        </div>
      )}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{form?.id ? `Editar ficha v${form.versao}` : "Nova ficha técnica"}</DialogTitle>
            <DialogDescription>Salva como rascunho. Somente o Gestor aprova — a versão aprovada é a usada nas novas ordens.</DialogDescription></DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Produto (intermediário ou acabado)</Label>
                <Select value={form.produto_id} disabled={!!form.id} onValueChange={(v) => setForm({ ...form, produto_id: v })}><SelectTrigger data-testid="ficha-produto"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{produtos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome} · {p.tipo === "ACABADO" ? "acabado" : "intermediário"}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Rendimento ({prodSel?.unidade_consumo || "un"})</Label><Input type="number" value={form.rendimento_qtd} onChange={(e) => setForm({ ...form, rendimento_qtd: Number(e.target.value) })} data-testid="ficha-rendimento" /></div>
              <div><Label>Peso por unidade (g)</Label><Input type="number" value={form.peso_unidade} onChange={(e) => setForm({ ...form, peso_unidade: Number(e.target.value) })} data-testid="ficha-peso" /></div>
              <div className="sm:col-span-2"><Label>Responsável</Label><Input value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} data-testid="ficha-responsavel" /></div>
              <IngredientRows form={form} setForm={setForm} itens={itens} />
              <div className="sm:col-span-2"><Label>Modo de preparo</Label><Textarea rows={3} value={form.modo_preparo} onChange={(e) => setForm({ ...form, modo_preparo: e.target.value })} data-testid="ficha-preparo" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-ficha">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar rascunho"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
